import { createKeepAliveOutbound } from '../api/outbound/commonOutbounds';
import { maybeConsoleLogOutbound } from '../utils/consoleUtils';
import { getTopWindow, isLocalhostEnvironment, isTestEnvironment } from '../utils/utils';
const KEEP_ALIVE_FREQUENCY_MS = 50000;
function getSimulatedLagMs() {
    const params = new URLSearchParams(window.location.search);
    if (params.has('lag')) {
        return Number(params.get('lag'));
    }
    return 0;
}
class WebSocketManager {
    constructor({ instanceId, onSocketMessage, onSocketClosed }) {
        this.keepAliveInterval = undefined;
        this.instanceId = instanceId;
        this.onSocketMessage = onSocketMessage;
        this.onSocketClosed = onSocketClosed;
        this.simulatedLagMs = getSimulatedLagMs();
        this.initWindowFns(instanceId);
    }
    async socketSend(message) {
        await this.ensureSocketIsOpen();
        if (!this.simulatedLagMs) {
            this.sendNow(message);
        }
        else {
            setTimeout(() => {
                this.sendNow(message);
            }, this.simulatedLagMs);
        }
    }
    closeSocket() {
        console.log('closing socket');
        this.socket?.close();
        this.socket = undefined;
    }
    setOnSocketMessage(onSocketMessage) {
        this.onSocketMessage = onSocketMessage;
    }
    setOnSocketClosed(onSocketClosed) {
        this.onSocketClosed = onSocketClosed;
    }
    initWindowFns(instanceId) {
        if (!isTestEnvironment()) {
            return;
        }
        // Expose the mock inbound handler on window for special testing use cases:
        // Ensure instance functions container exists:
        const topWindow = getTopWindow();
        topWindow.instanceFns = topWindow.instanceFns || [];
        topWindow.instanceFns[instanceId] = topWindow.instanceFns[instanceId] || {};
        const instanceFns = topWindow.instanceFns[instanceId];
        if (instanceFns) {
            instanceFns.handleMockInbound = this.handleMockInbound.bind(this);
            instanceFns.socketSend = this.socketSend.bind(this);
            instanceFns.closeSocket = this.closeSocket.bind(this);
        }
    }
    getWsUrl() {
        const url = new URL(location.href);
        const isSecure = url.protocol.toLowerCase().includes('https');
        const protocol = isSecure ? 'wss' : 'ws';
        const wsPortSuffix = isLocalhostEnvironment() ? ':8080' : '';
        return `${protocol}://${url.hostname}${wsPortSuffix}/triplejack/ws?instanceId=${this.instanceId}`;
    }
    sendKeepAlive() {
        this.socket?.send(createKeepAliveOutbound());
    }
    startKeepAliveInterval() {
        // TODO: Consider sending a 'keepalive:' only if it has been two minutes since the last outbound and/or inbound of any type.
        this.keepAliveInterval = window.setInterval(this.sendKeepAlive.bind(this), KEEP_ALIVE_FREQUENCY_MS);
    }
    stopKeepAliveInterval() {
        if (this.keepAliveInterval) {
            window.clearInterval(this.keepAliveInterval);
            this.keepAliveInterval = undefined;
        }
    }
    handleSocketOpened() {
        this.startKeepAliveInterval();
    }
    handleSocketClosed() {
        this.stopKeepAliveInterval();
        this.onSocketClosed();
    }
    handleSocketMessage(event) {
        const socketSend = this.socketSend.bind(this);
        this.onSocketMessage(event.data, socketSend);
    }
    /**
     * A special testing utility to allow simulating an arbitrary inbound (or "server outbound")
     * that won't actually come from the server. Useful for testing scenarios like the email
     * verification flow when no actual emails are sent in a test environment.
     */
    handleMockInbound(inbound) {
        this.handleSocketMessage({ data: inbound });
    }
    async openSocket() {
        this.closeSocket();
        const newSocket = new WebSocket(this.getWsUrl());
        newSocket.addEventListener('message', this.handleSocketMessage.bind(this));
        newSocket.addEventListener('close', this.handleSocketClosed.bind(this));
        this.socket = newSocket;
        return new Promise((resolve) => {
            newSocket.addEventListener('open', () => {
                this.handleSocketOpened();
                resolve();
            });
        });
    }
    async ensureSocketIsOpen() {
        if (!this.socket) {
            await this.openSocket();
        }
    }
    sendNow(message) {
        if (this.socket) {
            maybeConsoleLogOutbound(message, this.instanceId);
            this.socket.send(message);
        }
    }
}
export default WebSocketManager;
